import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

const sessionKey = "trailhead-chat-session";

export default function ChatBubble() {
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Howdy! I'm the Trailhead Taps assistant. Ask me about pricing, what's included, availability, or anything about renting our tap trailer." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(localStorage.getItem(sessionKey) || `s_${Date.now()}_${Math.random().toString(36).slice(2,8)}`);
  const scrollRef = useRef(null);

  useEffect(() => { localStorage.setItem(sessionKey, sessionId.current); }, []);
  useEffect(() => { scrollRef.current?.scrollTo({ top: 99999, behavior: "smooth" }); }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setLoading(true);
    try {
      const res = await api.post("/chat", { session_id: sessionId.current, message: text });
      setMessages((m) => [...m, { role: "assistant", text: res.data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: "Hmm, couldn't reach the bartender. Try again or submit an inquiry below." }]);
    } finally { setLoading(false); }
  };

  return (
    <>
      {loc.pathname !== "/login" && !open && (
        <button
          data-testid="chat-toggle"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 btn-orange rounded-full h-16 w-16 flex items-center justify-center shadow-xl"
          aria-label="Open chat"
        >
          <MessageCircle className="h-7 w-7" />
        </button>
      )}
      {loc.pathname !== "/login" && open && (
        <div data-testid="chat-window" className="fixed bottom-6 right-6 z-50 w-[92vw] max-w-md h-[70vh] max-h-[600px] paper-card flex flex-col overflow-hidden shadow-2xl">
          <div className="wood-bg px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-[#c97b3a] flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              <div>
                <div className="font-display text-lg text-[#F8F5F0] leading-tight">Ask the Bartender</div>
                <div className="text-[11px] text-[#e8dcc4]/80">AI-powered by Claude</div>
              </div>
            </div>
            <button data-testid="chat-close-button" onClick={() => setOpen(false)} className="text-[#F8F5F0]/80 hover:text-white">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F8F5F0]" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "bg-[#1f2e1f] text-[#F8F5F0] rounded-br-sm" : "bg-white border border-[#dcd3c6] text-[#1f2e1f] rounded-bl-sm"}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-[#dcd3c6] rounded-2xl px-4 py-2.5 text-sm text-[#8c8577]">Pouring a thought…</div>
              </div>
            )}
          </div>
          <div className="border-t border-[#dcd3c6] p-3 bg-white flex gap-2">
            <Input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask anything about renting..."
              disabled={loading}
            />
            <Button data-testid="chat-send" onClick={send} disabled={loading} className="btn-orange">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
